"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, X, ChevronDown, Calculator, GitCompare, BookOpen, Library, Sparkles } from "lucide-react";

import { countries } from "@/lib/country/registry";
import { calculatorRegistry } from "@/lib/constants/calculators";
import BrandMark from "./BrandMark";
import CountryFlag from "@/components/ui/CountryFlag";
import ThemeToggle from "./ThemeToggle";
import GlobalSearch from "./GlobalSearch";

const detectCountryFromSlug = (slug: string): string | null => {
  const lower = slug.toLowerCase();
  if (
    lower.includes("trump") ||
    lower.includes("401k") ||
    lower.includes("roth") ||
    lower.includes("529") ||
    lower.includes("fafsa") ||
    lower.includes("hsa") ||
    lower.includes("irs") ||
    lower.includes("form-843")
  ) {
    return "us";
  }
  if (lower.includes("kiwisaver") || lower.endsWith("-nz") || lower.includes("-nz-") || lower.includes("nz-")) return "nz";
  if (lower.includes("gst-rebate") || lower.includes("tfsa") || lower.includes("rrsp") || lower.includes("fhsa") || lower.endsWith("-ca") || lower.includes("-ca-") || lower.includes("canada")) return "ca";
  if (lower.includes("isa") || lower.includes("sipp") || lower.endsWith("-uk") || lower.includes("-uk-") || lower.includes("national-insurance") || lower.includes("paye")) return "gb";
  if (lower.includes("aktivrente") || lower.includes("riester") || lower.includes("einkommensteuer") || lower.endsWith("-de") || lower.includes("-de-") || lower.includes("germany")) return "de";
  if (lower.includes("pillar") || lower.endsWith("-ch") || lower.includes("-ch-") || lower.includes("switzerland")) return "ch";
  if (lower.includes("cpf") || lower.endsWith("-sg") || lower.includes("-sg-") || lower.includes("singapore")) return "sg";
  if (lower.includes("nps") || lower.includes("ups") || lower.endsWith("-in") || lower.includes("-in-") || lower.includes("elss") || lower.includes("ppf") || lower.includes("india")) return "in";
  return null;
};

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(countries[0]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Close country dropdown when clicking outside
  useEffect(() => {
    if (!countryDropdownOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".country-selector-container")) {
        setCountryDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [countryDropdownOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".sticky-header")) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [mobileMenuOpen]);

  // Extract country from pathname (e.g. /us/tax/income-tax -> us)
  useEffect(() => {
    const pathParts = pathname.split("/");
    const countryCode = pathParts[1];
    
    // Check if we are on a comparison page or blog page and lock the region
    if (countryCode === "compare" && pathParts[2]) {
      const slug = pathParts[2];
      const detected = detectCountryFromSlug(slug);
      if (detected) {
        const matchedCountry = countries.find((c) => c.code === detected);
        if (matchedCountry) {
          setActiveCountry(matchedCountry);
          return;
        }
      }
    } else if (countryCode === "blog" && pathParts[2]) {
      const slug = pathParts[2];
      const detected = detectCountryFromSlug(slug);
      if (detected) {
        const matchedCountry = countries.find((c) => c.code === detected);
        if (matchedCountry) {
          setActiveCountry(matchedCountry);
          return;
        }
      }
    }

    const matchedCountry = countries.find((c) => c.code === countryCode);
    if (matchedCountry) {
      setActiveCountry(matchedCountry);
      localStorage.setItem("selectedCountry", countryCode);
    } else {
      // Look for persisted preference
      const persisted = localStorage.getItem("selectedCountry");
      const matchedPersisted = countries.find((c) => c.code === persisted);
      if (matchedPersisted) {
        setActiveCountry(matchedPersisted);
      }
    }
  }, [pathname]);

  // Smart header: hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Always show header at the very top
      if (currentScrollY < 10) {
        setIsHeaderVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }
      
      // Scrolling down - hide header
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }
      // Scrolling up - show header
      else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleCountryChange = (country: typeof countries[0]) => {
    setCountryDropdownOpen(false);
    setActiveCountry(country);
    localStorage.setItem("selectedCountry", country.code);
    
    // Redirect to the same path but for the new country
    const pathParts = pathname.split("/");
    if (countries.some((c) => c.code === pathParts[1])) {
      const slug = pathParts[3]; // e.g., "income-tax"
      const category = pathParts[2]; // e.g., "tax"
      
      // If on a calculator page, check if calculator exists for new country
      if (slug && category) {
        const calc = calculatorRegistry.find(c => c.slug === slug);
        if (calc) {
          const isSupported = calc.countries === 'all' || calc.countries.includes(country.code);
          if (isSupported) {
            // Calculator exists for new country, keep the path
            pathParts[1] = country.code;
            router.push(pathParts.join("/"));
            return;
          }
          // Calculator doesn't support new country, go to country homepage
          router.push(`/${country.code}`);
          return;
        }
      }
      
      // Not on calculator page or couldn't determine - try to update country code
      pathParts[1] = country.code;
      router.push(pathParts.join("/"));
    } else {
      router.push(`/${country.code}`);
    }
  };

  const isLinkActive = (href: string, label: string) => {
    if (label === "Calculators") {
      return (
        pathname === `/${activeCountry.code}` ||
        (pathname.startsWith(`/${activeCountry.code}/`) && !pathname.includes("/glossary"))
      );
    }
    if (label === "Compare") {
      return pathname.startsWith("/compare");
    }
    if (label === "Blog") {
      return pathname.startsWith("/blog") || pathname.startsWith("/authors");
    }
    if (label === "Glossary") {
      return pathname.includes("/glossary");
    }
    if (label === "Daily") {
      return pathname.startsWith("/daily");
    }
    return pathname === href;
  };

  const navLinks = [
    { label: "Calculators", href: `/${activeCountry.code}`, icon: Calculator },
    { label: "Compare", href: "/compare", icon: GitCompare },
    { label: "Blog", href: "/blog", icon: BookOpen },
    { label: "Glossary", href: `/${activeCountry.code}/glossary`, icon: Library },
    { label: "Daily", href: "/daily", icon: Sparkles },
  ];

  return (
    <header className={`sticky-header ${isHeaderVisible ? 'header-visible' : 'header-hidden'}`}>
      <div className="header-container">
        <BrandMark />

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          {navLinks.map((link) => {
            const Icon = link.icon;
            const active = isLinkActive(link.href, link.label);
            return (
              <Link key={link.label} href={link.href} className={`nav-link ${active ? "active" : ""}`}>
                <Icon size={18} />
                <span>{link.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Header Actions */}
        <div className="header-actions">
          <GlobalSearch activeCountryCode={activeCountry.code} />

          {/* Country Selector Dropdown */}
          <div className="country-selector-container">
            <button
              onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              className="country-selector-btn"
              aria-haspopup="listbox"
              aria-expanded={countryDropdownOpen}
            >
              <CountryFlag code={activeCountry.code} name={activeCountry.name} className="country-flag-image-sm" eager />
              <span className="country-name-text">{activeCountry.name}</span>
              <ChevronDown size={16} className={`chevron-icon ${countryDropdownOpen ? "open" : ""}`} />
            </button>

            {countryDropdownOpen && (
              <div className="country-dropdown">
                <div className="country-dropdown-header">Select Country</div>
                <ul role="listbox" className="country-list">
                  {countries.map((country) => (
                    <li key={country.code} role="option" aria-selected={country.code === activeCountry.code}>
                      <button
                        onClick={() => handleCountryChange(country)}
                        className={`country-option-btn ${country.code === activeCountry.code ? "active" : ""}`}
                      >
                        <CountryFlag code={country.code} name={country.name} className="country-flag-image-sm" />
                        <span className="country-label">{country.name} ({country.currency})</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <ThemeToggle />

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-btn"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-drawer anim-slide-up">
          <nav className="mobile-nav">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isLinkActive(link.href, link.label);
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`mobile-nav-link ${active ? "active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon size={20} />
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      <style jsx>{`
        .sticky-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background-color: var(--bg-header);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border-color);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        
        .sticky-header.header-hidden {
          transform: translateY(-100%);
        }
        
        .sticky-header.header-visible {
          transform: translateY(0);
        }
        .header-container {
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          width: 100%;
          height: 4rem;
          padding: 0 var(--space-4);
        }
        @media (min-width: 768px) {
          .header-container {
            height: 4.5rem;
            padding: 0 var(--space-6);
          }
        }
        .header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: var(--space-2);
        }
        @media (min-width: 640px) {
          .header-actions {
            gap: var(--space-3);
          }
        }
        :global(.desktop-nav) {
          display: none;
          align-items: center;
          justify-content: center;
          gap: var(--space-4);
        }
        /* The 5-link nav + search + country + toggle physically cannot fit
           between 768px and ~1024px (the old breakpoint) — the header
           overflowed at intermediate widths. Nav now appears at 1024px, with
           a compact band up to 1439px. Below 1024px the hamburger pattern
           (which fits) stays on. */
        @media (min-width: 1024px) {
          :global(.desktop-nav) {
            display: flex;
          }
        }
        @media (min-width: 1024px) and (max-width: 1439px) {
          :global(.desktop-nav) {
            gap: var(--space-2);
          }
          :global(.nav-link) {
            padding: 0.5rem 0.625rem;
          }
        }
        :global(.nav-link) {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 0.5rem !important;
          font-family: var(--font-heading);
          font-weight: var(--fw-bold);
          font-size: var(--text-sm);
          color: var(--text-secondary) !important;
          transition: all var(--transition-fast);
          padding: 0.5rem 0.875rem;
          border-radius: var(--radius-md);
          position: relative;
          text-decoration: none;
          white-space: nowrap;
          border: 1px solid transparent;
        }
        :global(.nav-link:hover) {
          color: var(--text-primary) !important;
          background-color: var(--bg-surface-hover);
          transform: translateY(-1px);
        }
        :global(.nav-link.active) {
          color: var(--primary) !important;
          font-weight: var(--fw-extrabold) !important;
          background-color: var(--primary-a12) !important;
          border: 1px solid var(--primary-a28) !important;
          box-shadow: 0 1px 4px var(--primary-a12);
        }
        :global(.nav-link.active svg) {
          color: var(--primary) !important;
        }
        :global([data-theme='dark']) :global(.nav-link.active) {
          background-color: var(--primary-a18) !important;
          border: 1px solid var(--primary-a35) !important;
          color: var(--primary) !important;
        }
        :global([data-theme='dark']) :global(.nav-link.active svg) {
          color: var(--primary) !important;
        }
        :global(.nav-link:hover svg) {
          transform: scale(1.1);
        }
        :global(.nav-link svg) {
          flex-shrink: 0;
          transition: transform var(--transition-fast);
        }
        :global(.nav-link span) {
          line-height: 1;
        }
        .country-selector-container {
          position: relative;
        }
        .country-selector-btn {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          background-color: var(--control-bg);
          border: 1px solid var(--control-border);
          padding: 0 var(--space-3);
          border-radius: var(--radius-full);
          color: var(--text-primary);
          cursor: pointer;
          font-family: var(--font-body);
          font-size: var(--text-sm);
          transition: all var(--transition-fast);
          /* Use transform and opacity for better compositing */
          will-change: transform, opacity;
          height: 38px;
          white-space: nowrap;
        }
        @media (min-width: 640px) {
          .country-selector-btn {
            padding: 0 var(--space-4);
            gap: var(--space-3);
            height: 42px;
          }
        }
        .country-selector-btn:hover {
          background-color: var(--control-bg-hover);
          border-color: var(--control-border-hover);
        }
        .country-selector-btn :global(.country-flag-image) {
          display: block;
        }
        .country-name-text {
          display: none;
          line-height: 1;
        }
        @media (min-width: 640px) {
          .country-name-text {
            display: block;
            line-height: 1;
          }
        }
        /* Compact desktop band: flag-only country button so the nav fits. */
        @media (min-width: 1024px) and (max-width: 1439px) {
          .country-name-text {
            display: none;
          }
        }
        .chevron-icon {
          transition: transform var(--transition-fast);
        }
        .chevron-icon.open {
          transform: rotate(180deg);
        }
        .country-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + var(--space-2));
          width: 250px;
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color-hover);
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-xl);
          overflow: hidden;
          animation: slideUp var(--transition-fast) forwards;
        }
        .country-dropdown-header {
          padding: var(--space-3) var(--space-4);
          font-family: var(--font-heading);
          font-size: var(--text-2xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          color: var(--text-muted);
          border-bottom: 1px solid var(--border-color);
          background-color: var(--bg-surface-hover);
        }
        .country-list {
          list-style: none;
          max-height: 300px;
          overflow-y: auto;
        }
        .country-option-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: var(--space-5);
          padding: var(--space-3) var(--space-4);
          background: transparent;
          border: none;
          color: var(--text-secondary);
          text-align: left;
          cursor: pointer;
          font-family: var(--font-body);
          font-size: var(--text-sm);
          transition: all var(--transition-fast);
        }
        .country-option-btn:hover {
          background-color: var(--bg-surface-hover);
          color: var(--text-primary);
        }
        .country-option-btn.active {
          color: var(--primary);
          font-weight: var(--fw-bold);
          background-color: var(--primary-a05);
        }
        .mobile-menu-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: var(--text-primary);
          cursor: pointer;
          padding: 0.5rem;
          margin: -0.5rem -0.25rem -0.5rem 0;
          min-width: 44px;
          min-height: 44px;
        }
        @media (min-width: 1024px) {
          .mobile-menu-btn {
            display: none;
          }
        }
        .mobile-drawer {
          position: absolute;
          top: 4rem;
          left: 0;
          right: 0;
          background-color: var(--bg-surface);
          border-bottom: 1px solid var(--border-color);
          padding: var(--space-5) var(--space-4);
          box-shadow: var(--shadow-lg);
        }
        @media (min-width: 768px) {
          .mobile-drawer {
            top: 4.5rem;
          }
        }
        @media (min-width: 1024px) {
          .mobile-drawer {
            display: none;
          }
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
        :global(.mobile-nav-link) {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-family: var(--font-heading);
          font-weight: var(--fw-bold);
          font-size: var(--text-md);
          color: var(--text-secondary);
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
          text-decoration: none;
        }
        :global(.mobile-nav-link:hover) {
          color: var(--text-primary);
          background-color: var(--bg-surface-hover);
          transform: translateX(4px);
        }
        :global(.mobile-nav-link.active) {
          color: var(--primary) !important;
          font-weight: var(--fw-extrabold) !important;
          background-color: var(--primary-a12) !important;
          border-left: 4px solid var(--primary) !important;
        }
        :global(.mobile-nav-link.active svg) {
          color: var(--primary) !important;
        }
        :global(.mobile-nav-link svg) {
          flex-shrink: 0;
          transition: transform var(--transition-fast);
        }
        :global(.mobile-nav-link:hover svg) {
          transform: scale(1.1);
        }
        :global(.mobile-nav-link span) {
          line-height: 1;
        }
      `}</style>
    </header>
  );
}
